// El informe de contabilidad: cliente · fecha · concepto · importe.
//
// Las CUATRO columnas que pidió dirección, y la razón de que esto sea una RPC y
// no una consulta desde aquí: el dinero vive en `orders.items`, un jsonb, con
// los packs guardando dentro lo que contienen —8 de las 19 matrículas están
// ahí— y en DOS formatos distintos de `components`. Reproducir eso en el
// navegador sería tener la regla en dos sitios; la de verdad está en la mig 339
// y es la que el pgTAP mide.
//
// El error NO se traga a propósito: hasta que la 339 esté aplicada, PostgREST
// responde 404 y pintar «0 €» sería decirle a dirección que no se ha vendido
// nada.
import { supabase } from './supabase';

export interface FilaDeContabilidad {
  cliente: string;
  fecha: string;
  concepto: string;
  importe_cents: number;
}

export type ResultadoContabilidad =
  | { estado: 'ok'; filas: FilaDeContabilidad[] }
  | { estado: 'error'; mensaje: string };

export async function leerContabilidadPorConcepto(
  desde?: string,
  hasta?: string
): Promise<ResultadoContabilidad> {
  const { data, error } = await supabase.rpc('contabilidad_por_concepto', {
    p_desde: desde,
    p_hasta: hasta,
  });
  if (error) {
    return { estado: 'error', mensaje: error.message };
  }
  return { estado: 'ok', filas: data ?? [] };
}

// El CSV. Cuatro columnas y nada más, que es lo que se pidió.
//
// El importe va con COMA decimal: el destino es una hoja de cálculo en
// castellano, y «70.00» ahí se lee como setenta mil. Por eso el separador de
// campos es `;` y no `,` — con coma decimal y coma separadora, la fila se
// parte donde no debe.
export function csvDeContabilidad(filas: FilaDeContabilidad[]): string {
  const lineas = ['Cliente;Fecha;Concepto;Importe total'];
  for (const f of filas) {
    const [a, m, d] = f.fecha.split('-');
    const euros = (f.importe_cents / 100).toFixed(2).replace('.', ',');
    lineas.push(`"${f.cliente.replace(/"/g, '""')}";${d}/${m}/${a};${f.concepto};${euros}`);
  }
  return lineas.join('\n');
}

// ── El mes ───────────────────────────────────────────────────────────────────
// Se filtra en el CLIENTE, no en la RPC, y es una decisión medida: la página ya
// se trae el informe entero en una lectura al montar, y cada fila lleva su
// fecha —el último día del mes del cobro—, así que los meses con ventas salen
// de lo que ya está en memoria. Cero lecturas nuevas por cambiar de mes.
//
// La RPC acepta `p_desde`/`p_hasta` y aquí no se usan a propósito: el día que
// esto sean miles de filas en vez de 19, el filtro se mueve allí sin tocar la
// pantalla. Hoy mover el filtro al servidor sería un viaje de red por cada
// cambio del selector, para filtrar 19 filas.

export const TODOS_LOS_MESES = 'todo';

// Los meses que TIENEN ventas, del más reciente al más antiguo. No un
// calendario: un desplegable con doce meses de los que diez están vacíos hace
// que el usuario tenga que probar para saber dónde hay algo.
export function mesesConVentas(filas: FilaDeContabilidad[]): string[] {
  return [...new Set(filas.map((f) => f.fecha.slice(0, 7)))].sort().reverse();
}

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

// `2026-10` → `octubre 2026`. Sin `Date` ni `Intl`: `new Date('2026-10-31')` se
// parsea como medianoche UTC y en un huso negativo pinta el 30, no el 31. Es el
// mismo motivo por el que el CSV parte la cadena en vez de construir una fecha.
export function etiquetaDeMes(mes: string): string {
  if (mes === TODOS_LOS_MESES) return 'Todos los meses';
  const [a, m] = mes.split('-');
  return `${MESES[Number(m) - 1]} ${a}`;
}

export function filasDelMes(filas: FilaDeContabilidad[], mes: string): FilaDeContabilidad[] {
  if (mes === TODOS_LOS_MESES) return filas;
  return filas.filter((f) => f.fecha.slice(0, 7) === mes);
}

// El nombre lleva el mes dentro para que tres descargas no sean
// `contabilidad.csv`, `contabilidad (1).csv` y `contabilidad (2).csv` en la
// carpeta de Jose. En ISO y no «octubre», que así se ordenan solas.
export function nombreDelCsv(mes: string): string {
  return `contabilidad_${mes}.csv`;
}
